import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { useSite, FALLBACK_SETTINGS } from "@/lib/content";

const SECTIONS = [
  {
    title: "1. Information We Collect",
    body: "When you submit a project inquiry through our contact form, we collect the information you provide: your name, company, business email address, phone number (if provided), and details about your project. We also collect standard technical information (such as IP address) for security and abuse-prevention purposes.",
  },
  {
    title: "2. How We Use Information",
    body: "We use the information you submit solely to respond to your inquiry, evaluate potential projects, and communicate with you about our services. We do not sell, rent, or share your personal information with third parties for their marketing purposes.",
  },
  {
    title: "3. Data Storage & Security",
    body: "Inquiry information is stored securely in our systems with access restricted to authorized personnel. We apply industry-standard security practices including encrypted connections (HTTPS), access controls, and audit logging for administrative actions.",
  },
  {
    title: "4. Email Communications",
    body: "Submitting the contact form triggers a notification email to our team containing the details you provided. We may reply to you directly using the email address you supplied. We do not add you to marketing lists without your consent.",
  },
  {
    title: "5. Cookies & Analytics",
    body: "This website may use privacy-conscious analytics to understand how visitors use the site and to improve performance. Administrative areas use secure, HttpOnly cookies strictly for authentication.",
  },
  {
    title: "6. Data Retention",
    body: "We retain inquiry information for as long as needed to respond to your request and maintain a record of our communications. You may request deletion of your information at any time by contacting us.",
  },
  {
    title: "7. Your Rights",
    body: "You may request access to, correction of, or deletion of your personal information by contacting us at the email address below. We will respond to verified requests within a reasonable timeframe.",
  },
  {
    title: "8. Changes to This Policy",
    body: "We may update this Privacy Policy from time to time. Material changes will be reflected on this page with an updated effective date.",
  },
];

export default function Privacy() {
  const { data } = useSite();
  const s = { ...FALLBACK_SETTINGS, ...(data?.settings || {}) };
  const seo = data?.settings?.seo?.["/privacy"];

  return (
    <>
      <Seo
        title={seo?.title || "Privacy Policy | LogiTech Nexus"}
        siteName="LogiTech Nexus"
        description={seo?.description || "How LogiTech Nexus collects, uses and protects information submitted through this website."}
      />
      <PageHero eyebrow="Legal" title="Privacy Policy" subtitle="Effective date: January 1, 2026" testid="privacy-hero" />
      <section className="bg-void pb-28" data-testid="privacy-content">
        <div className="mx-auto max-w-3xl space-y-10 px-6 lg:px-10">
          {SECTIONS.map((sec) => (
            <div key={sec.title}>
              <h2 className="font-display text-xl font-semibold text-white">{sec.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{sec.body}</p>
            </div>
          ))}
          <div className="rounded-xl border border-slate-800/70 bg-navy/50 p-6">
            <h2 className="font-display text-xl font-semibold text-white">Contact Us</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              {s.companyName}
              <br />
              {s.addressLine1}, {s.addressLine2}, {s.addressCountry}
              <br />
              <a href={`mailto:${s.email}`} className="text-blue-400 transition-colors hover:text-blue-300" data-testid="privacy-email-link">
                {s.email}
              </a>
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
